'use client';

import * as React from 'react';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  Package,
  CreditCard,
  Workflow,
  Rocket,
  RefreshCw,
  ExternalLink,
  Play,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/api/trpc';

// ============================================================================
// TYPES
// ============================================================================

type SeedingStatus = 'pending' | 'completed' | 'failed';

interface SeedingStep {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  status: SeedingStatus | undefined;
}

interface StoreRoutes {
  STOREFRONT: () => string;
}

interface DemoSeedingProgressProps {
  /** Store ID to poll seeding status for */
  storeId: string;
  /** Store routes for navigation links */
  storeRoutes: StoreRoutes;
  /** Callback when all seeding tasks complete */
  onComplete?: () => void;
  /** Callback when products seeding completes (for list refresh) */
  onProductsComplete?: () => void;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const POLLING_INTERVAL_MS = 2000;
const COMPLETION_DELAY_MS = 1500;

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Displays demo store seeding progress with real-time status updates.
 * Shows compact banners for loading, in-progress, completed, and failed states.
 */
export function DemoSeedingProgress({
  storeId,
  storeRoutes,
  onComplete,
  onProductsComplete,
  className,
}: DemoSeedingProgressProps) {
  const [hasNotifiedComplete, setHasNotifiedComplete] = React.useState(false);
  const prevProductsStatusRef = React.useRef<SeedingStatus | undefined>(undefined);

  // Poll for seeding status
  const {
    data: seedingStatus,
    isLoading,
    refetch,
  } = trpc.stores.getDemoSeedingStatus.useQuery(
    { storeId },
    {
      refetchInterval: (query) => {
        const data = query.state.data;
        if (!data) return POLLING_INTERVAL_MS;

        const isComplete =
          data.products === 'completed' &&
          data.paymentFlow === 'completed' &&
          data.funnel === 'completed';

        const hasFailed =
          data.products === 'failed' ||
          data.paymentFlow === 'failed' ||
          data.funnel === 'failed';

        // Stop polling when done or failed
        return isComplete || hasFailed ? false : POLLING_INTERVAL_MS;
      },
      enabled: Boolean(storeId),
    },
  );

  // Build step status array
  const steps: SeedingStep[] = React.useMemo(
    () => [
      {
        id: 'products',
        label: 'Products',
        icon: Package,
        status: seedingStatus?.products as SeedingStatus | undefined,
      },
      {
        id: 'paymentFlow',
        label: 'Payments',
        icon: CreditCard,
        status: seedingStatus?.paymentFlow as SeedingStatus | undefined,
      },
      {
        id: 'funnel',
        label: 'Checkout',
        icon: Workflow,
        status: seedingStatus?.funnel as SeedingStatus | undefined,
      },
    ],
    [seedingStatus],
  );

  // Derived state
  const completedCount = steps.filter((s) => s.status === 'completed').length;
  const failedSteps = steps.filter((s) => s.status === 'failed');
  const isAllCompleted = completedCount === steps.length;
  const hasAnyFailed = failedSteps.length > 0;

  // Notify parent when products complete (for list refresh)
  React.useEffect(() => {
    const currentStatus = seedingStatus?.products as SeedingStatus | undefined;
    const prevStatus = prevProductsStatusRef.current;

    // Detect transition to 'completed'
    if (currentStatus === 'completed' && prevStatus !== 'completed' && onProductsComplete) {
      onProductsComplete();
    }

    prevProductsStatusRef.current = currentStatus;
  }, [seedingStatus?.products, onProductsComplete]);

  // Notify parent when all tasks complete (with delay for UX)
  React.useEffect(() => {
    if (isAllCompleted && !hasNotifiedComplete && onComplete) {
      setHasNotifiedComplete(true);
      const timer = setTimeout(onComplete, COMPLETION_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [isAllCompleted, hasNotifiedComplete, onComplete]);

  // Don't render if no seeding is in progress
  if (!seedingStatus && !isLoading) {
    return null;
  }

  // Loading skeleton
  if (isLoading) {
    return <LoadingSkeleton className={className} />;
  }

  // Completed state
  if (isAllCompleted) {
    return (
      <CompletedBanner
        previewUrl={seedingStatus?.previewUrl}
        storefrontUrl={storeRoutes.STOREFRONT()}
        className={className}
      />
    );
  }

  // Failed state
  if (hasAnyFailed) {
    return (
      <FailedBanner
        failedLabels={failedSteps.map((s) => s.label)}
        onRetry={refetch}
        className={className}
      />
    );
  }

  // In progress state
  return (
    <ProgressBanner
      steps={steps}
      completedCount={completedCount}
      className={className}
    />
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function LoadingSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('animate-pulse rounded-lg border bg-muted/30 p-4', className)}>
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-full bg-muted" />
        <div className="h-4 w-32 rounded bg-muted" />
      </div>
    </div>
  );
}

function CompletedBanner({
  previewUrl,
  storefrontUrl,
  className,
}: {
  previewUrl?: string;
  storefrontUrl: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-4',
        className,
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100">
            <Rocket className="h-[18px] w-[18px] text-emerald-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-900">
              Your demo store is ready!
            </p>
            <p className="text-xs text-emerald-600">
              Products, payments, and checkout are all set up.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {previewUrl && (
            <a href={previewUrl} target="_blank" rel="noopener noreferrer">
              <Button
                size="sm"
                className="bg-emerald-600 text-white hover:bg-emerald-700"
              >
                <Play className="mr-1.5 h-3.5 w-3.5" />
                Test Checkout
              </Button>
            </a>
          )}
          <Link to={storefrontUrl}>
            <Button
              size="sm"
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
            >
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              View Funnel
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function FailedBanner({
  failedLabels,
  onRetry,
  className,
}: {
  failedLabels: string[];
  onRetry: () => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border border-red-200 bg-gradient-to-r from-red-50 to-orange-50 p-4',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-red-100">
            <XCircle className="h-[18px] w-[18px] text-red-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-red-900">
              Setup encountered an issue
            </p>
            <p className="text-xs text-red-600">
              {failedLabels.join(', ')} failed to set up.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onRetry}
          className="border-red-300 text-red-700 hover:bg-red-100"
        >
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    </div>
  );
}

function ProgressBanner({
  steps,
  completedCount,
  className,
}: {
  steps: SeedingStep[];
  completedCount: number;
  className?: string;
}) {
  const progressPercent = (completedCount / steps.length) * 100;

  return (
    <div
      className={cn(
        'rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 via-indigo-50 to-violet-50 p-4',
        className,
      )}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        {/* Left: Icon and steps */}
        <div className="flex items-center gap-4">
          {/* Animated loading icon */}
          <div className="relative flex h-9 w-9 items-center justify-center">
            <div className="absolute inset-0 animate-ping rounded-full bg-blue-400 opacity-20" />
            <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-blue-100">
              <Loader2 className="h-[18px] w-[18px] animate-spin text-blue-600" />
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-slate-900">
              Setting up your demo store...
            </p>
            {/* Horizontal step indicators */}
            <div className="mt-1.5 flex items-center gap-1">
              {steps.map((step, idx) => (
                <React.Fragment key={step.id}>
                  <StepPill step={step} />
                  {idx < steps.length - 1 && (
                    <StepConnector isComplete={step.status === 'completed'} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Progress counter */}
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-slate-600">
            {completedCount}/{steps.length}
          </span>
          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-blue-500 transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function StepPill({ step }: { step: SeedingStep }) {
  const Icon = step.icon;
  const isComplete = step.status === 'completed';
  const isPending = step.status === 'pending';

  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-all',
        isComplete && 'bg-emerald-100 text-emerald-700',
        isPending && 'bg-blue-100 text-blue-700',
        !isComplete && !isPending && 'bg-slate-100 text-slate-500',
      )}
    >
      {isComplete ? (
        <CheckCircle2 className="h-3 w-3" />
      ) : isPending ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <Icon className="h-3 w-3" />
      )}
      <span>{step.label}</span>
    </div>
  );
}

function StepConnector({ isComplete }: { isComplete: boolean }) {
  return (
    <div
      className={cn('h-px w-2', isComplete ? 'bg-emerald-300' : 'bg-slate-200')}
    />
  );
}
